import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";

import { useWishlistService } from "@api/hooks/wishlistService.hook";

import {
    PageTemplates,
    isPageTemplate,
} from "@utils/configuration/pageTemplate.helpers";
import { useIsClientSide } from "@utils/hooks/isClient.hook";

import CallService, { CallServiceKey } from "@services/call.service";
import FeatureToggles, {
    FeatureToggleKeys,
} from "@services/featureToggles.services";

import { useThunkDispatch } from "@redux/dispatcher";
import { getCustomer } from "@redux/modules/api/data/customer/customer.selectors";
import * as WishlistModule from "@redux/modules/api/data/wishlist/wishlist.module";
import * as WishlistSelectors from "@redux/modules/api/data/wishlist/wishlist.selectors";
import { WISHLIST_MESSAGE_ID } from "@redux/modules/api/data/wishlist/wishlist.utils";
import * as HeaderSelectors from "@redux/modules/value/header/header.selectors";

import {
    HeaderButton,
    HeaderButtonProps,
} from "@components/General/HeaderButton/headerButton.component";
import { Messages } from "@components/General/Messages/messages.container";

import "./wishlistHeaderButton.scss";

export type WishlistHeaderButtonContainerProps = HeaderButtonProps;

const WishlistHeaderButtonContainer = (
    props: WishlistHeaderButtonContainerProps,
) => {
    const { invert, text, link, large } = props;

    const isClientSide = useIsClientSide();
    const wishlists = useSelector(WishlistSelectors.getWishlists);
    const header = useSelector(HeaderSelectors.getHeader);
    const dispatch = useThunkDispatch();
    const customer = useSelector(getCustomer);
    const isLoggedIn = customer && !customer.guest;

    const [{ data }, loadWishlist] = useWishlistService(
        {
            url: "/products/count",
            method: "GET",
        },
        {
            manual: true,
        },
    );

    useEffect(() => {
        if (isClientSide) {
            if (FeatureToggles.isEnabled(FeatureToggleKeys.WISHLIST_BADGE)) {
                loadWishlist().then(
                    payload => {
                        if (payload.data > 0) {
                            CallService.enable(CallServiceKey.Wishlist);
                        } else {
                            CallService.disable(CallServiceKey.Wishlist);
                        }
                    },
                    () => {
                        CallService.enable(CallServiceKey.Wishlist);
                    },
                );
            } else {
                dispatch(WishlistModule.loadAll());
            }
        }
    }, [isClientSide]);

    const componentShouldInvert = isClientSide
        ? invert || header?.mobile
        : false;

    const productCount = wishlists.reduce(
        (acc, wishlist) => acc + wishlist?.products?.length || 0,
        0,
    );
    const itemCount = isClientSide ? (productCount ? productCount : data) : 0;

    const showMessagesContainer = !(
        isPageTemplate(PageTemplates.SHOPPING_CART) && isLoggedIn
    );

    return (
        <div className="mx-1">
            <HeaderButton
                {...props}
                invert={componentShouldInvert}
                text={text}
                hover={true}
                addCloseCallback={true}
                large={large}
                iconPrefix="lar"
                link={link}
                alerts={(itemCount || 0) < 100 ? itemCount : "+99"}
            ></HeaderButton>
            {isClientSide &&
                showMessagesContainer &&
                createPortal(
                    <Messages
                        messageid={WISHLIST_MESSAGE_ID}
                        className={
                            "wishlistHeaderButton__messages position-fixed mx-3"
                        }
                    />,
                    document.querySelector("body")!,
                )}
        </div>
    );
};

export { WishlistHeaderButtonContainer };
